import { useClaimConfidenceInfoByClaimID, useGetClaim } from 'api/resource';
import { ClaimConfidenceInfo } from '@billing/validation/intakeItemDocument';
import { Claim } from 'interfaces';
import { useEffect, useMemo, useState } from 'react';
import { ClaimTableRow, buildClaimTableRows } from '../../utils/claimTableRows';

interface UseClaimTableDataProps {
  claims: Claim[];
  editingID?: string;
}

interface UseClaimTableDataResult {
  tableData: Claim[];
  tableRows: ClaimTableRow[];
  confidenceByClaimID: Record<string, number | null>;
  confidenceMetadataByClaimID: Record<string, ClaimConfidenceInfo>;
  getClaimByID: (claimID: string) => Claim | undefined;
}

export const useClaimTableData = ({
  claims,
  editingID,
}: UseClaimTableDataProps): UseClaimTableDataResult => {
  const [tableClaims, setTableClaims] = useState<Claim[]>(claims);
  const { data: fetchedClaim } = useGetClaim({
    id: editingID || '',
    displayNotification: false,
  });

  // Sync the current page while preserving a deep-linked claim not on it.
  useEffect(() => {
    setTableClaims((previousClaims) => {
      const pageClaimIDs = new Set(claims.map((claim) => claim.id));
      const deepLinkedClaim =
        editingID &&
        previousClaims.find(
          (claim) => claim.id === editingID && !pageClaimIDs.has(claim.id)
        );
      const nextClaims = deepLinkedClaim
        ? [...claims, deepLinkedClaim]
        : [...claims];
      const sameOrder =
        nextClaims.length === previousClaims.length &&
        nextClaims.every(
          (claim, index) => claim.id === previousClaims[index]?.id
        );
      const hasChanges =
        !sameOrder ||
        nextClaims.some((claim, index) => claim !== previousClaims[index]);

      return hasChanges ? nextClaims : previousClaims;
    });
  }, [claims, editingID]);

  // Hydrate a deep-linked claim that is not part of the current server page.
  useEffect(() => {
    if (!editingID || fetchedClaim?.id !== editingID) return;
    if (claims.some((claim) => claim.id === editingID)) return;

    setTableClaims((previousClaims) => {
      const existingClaim = previousClaims.find(
        (claim) => claim.id === editingID
      );
      if (existingClaim === fetchedClaim) return previousClaims;
      if (!existingClaim) return [...previousClaims, fetchedClaim];
      return previousClaims.map((claim) =>
        claim.id === editingID ? fetchedClaim : claim
      );
    });
  }, [claims, editingID, fetchedClaim]);

  const { data: confidenceMetadataByClaimID = {} } =
    useClaimConfidenceInfoByClaimID({ claims: tableClaims });
  const confidenceByClaimID = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(confidenceMetadataByClaimID).map(
          ([claimID, metadata]) => [claimID, metadata.confidence]
        )
      ),
    [confidenceMetadataByClaimID]
  );

  const tableData = useMemo(() => tableClaims, [tableClaims]);
  const tableRows = useMemo<ClaimTableRow[]>(
    () => buildClaimTableRows(tableData),
    [tableData]
  );

  const getClaimByID = (claimID: string): Claim | undefined =>
    tableData.find((claim) => claim.id === claimID);

  return {
    tableData,
    tableRows,
    confidenceByClaimID,
    confidenceMetadataByClaimID,
    getClaimByID,
  };
};
